import { ArrowLeft, CalendarDays, CalendarRange, Clock3, Inbox, MailCheck } from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  formatRelativeTime,
  loadPersonOperations,
  shortThreadId,
  type TeamPerson,
  type WorkRecord
} from '../lib/liveOperations'

export function EmployeeDetail() {
  const { selectedTeamId } = useTeamScope()
  const { id } = useParams()
  const [person, setPerson] = useState<TeamPerson | null>(null)
  const [records, setRecords] = useState<WorkRecord[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    loadPersonOperations(id, selectedTeamId)
      .then(data => {
        setPerson(data.person)
        setRecords(data.personRecords)
      })
      .finally(() => setLoading(false))
  }, [id, selectedTeamId])

  if (!id) return <Navigate to="/app/team" replace />
  if (!loading && !person) return <Navigate to="/app/team" replace />
  if (!person) return <WorkspaceSkeleton rows={3}/>

  return <div className="stack-lg detail-page">
    <div className="detail-page-nav"><Link to="/app/team" className="back-link"><ArrowLeft size={17}/>Back to team</Link></div>

    <section className="detail-page-hero">
      <div className="detail-page-identity">
        <div className="avatar detail-avatar">{person.initials}</div>
        <div><span className="eyebrow">Customer email team member</span><h2>{person.displayName}</h2><p>{person.teamName}</p></div>
      </div>
      <div className="detail-page-badges">
        <Badge tone={person.status === 'recent' ? 'good' : 'neutral'}>{person.status === 'recent' ? 'Recent mailbox data' : person.status === 'earlier' ? 'Earlier mailbox data' : 'No mailbox data'}</Badge>
        <Badge tone="blue">{formatRelativeTime(person.lastActivityAt)}</Badge>
      </div>
    </section>

    <div className="detail-page-stat-grid">
      <div><MailCheck size={20}/><span>Customer replies sent</span><strong>{person.replied}</strong><small>{person.threadsWorked} measured customer emails</small></div>
      <div><Inbox size={20}/><span>Needs follow-through</span><strong>{person.open}</strong><small>Customer emails without a sent reply</small></div>
      <div><Clock3 size={20}/><span>Average response</span><strong>{formatDuration(person.averageQueueWaitSeconds)}</strong><small>Customer email received to first reply sent</small></div>
      <div><span>Response rate</span><strong>{person.threadsWorked ? `${Math.round((person.replied / person.threadsWorked) * 100)}%` : '—'}</strong><small>Measured customer emails with a sent reply</small></div>
    </div>

    <div className="two-col detail-page-grid">
      <Section title="Customer email work" subtitle="Recent customer emails handled today">
        <div className="detail-breakdown">
          {records.slice(0, 6).map(record =>
            <div key={record.external_thread_id}>
              <span>Email reference {shortThreadId(record.external_thread_id)}{record.mailbox_type === 'shared' ? ' · Shared inbox' : ''}</span>
              <strong>{record.work_status === 'replied' ? 'Replied' : 'Waiting'}</strong>
              {record.mailbox_type === 'shared' && record.attribution_confidence === 'high' && <small>Provider-verified handler attribution</small>}
            </div>
          )}
          {records.length === 0 && <p className="muted">No measured customer email work yet today.</p>}
        </div>
      </Section>

      <Section title="Employee history" subtitle="Open this employee’s recorded customer-email work over time">
        <div className="employee-history-card">
          <Link to={`/app/team/${person.userId}/records/daily`} className="employee-history-option">
            <CalendarDays size={19}/><div><strong>Daily records</strong><span>Every operating day and the customer-email records measured that day.</span></div>
          </Link>
          <Link to={`/app/team/${person.userId}/records/weekly`} className="employee-history-option">
            <CalendarRange size={19}/><div><strong>Weekly records</strong><span>Weekly totals inside each month, with the operating days and records underneath.</span></div>
          </Link>
          <Link to={`/app/team/${person.userId}/records/monthly`} className="employee-history-option">
            <Clock3 size={19}/><div><strong>Monthly records</strong><span>Month-by-month results with every measured operating day inside.</span></div>
          </Link>
        </div>
      </Section>
    </div>
  </div>
}
